import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ── Settings row (used inside hub pages) ─────────────────────────────────────

export interface SettingsRowDef {
  id: string;
  label: string;
  desc: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  path: string;
  /** Return true if visible. Defaults to always visible. */
  isVisible?: boolean;
}

export function SettingsRow({ row, onClick }: { row: SettingsRowDef; onClick: () => void }) {
  const Icon = row.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border)]
                 rounded-xl p-4 shadow-brand-sm
                 flex items-center gap-4
                 hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                 transition-all duration-150"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
        <Icon size={20} className="text-plum" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-small text-[var(--color-text-primary)]">{row.label}</p>
        <p className="text-[13px] text-[var(--color-text-secondary)] mt-0.5 leading-snug">{row.desc}</p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-slate" />
    </button>
  );
}

// ── Hub page wrapper ─────────────────────────────────────────────────────────

interface HubPageLayoutProps {
  title: string;
  subtitle: string;
  rows: SettingsRowDef[];
  /** Where the back button navigates. Defaults to '/admin'. */
  backPath?: string;
  /** Label shown on the back button. Defaults to 'Admin Panel'. */
  backLabel?: string;
}

export function HubPageLayout({ title, subtitle, rows, backPath = '/admin', backLabel = 'Admin Panel' }: HubPageLayoutProps) {
  const navigate = useNavigate();

  const visibleRows = rows.filter(r => r.isVisible !== false);

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-[600px] md:mx-auto">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="flex items-center gap-1 text-small text-slate hover:text-plum mb-4 -ml-1
                   transition-colors"
      >
        <ChevronLeft size={16} />
        {backLabel}
      </button>

      {/* Header */}
      <h1 className="font-display text-h1 text-[var(--color-text-primary)] mb-1">{title}</h1>
      <p className="text-small text-[var(--color-text-secondary)] mb-6">{subtitle}</p>

      {/* Settings list */}
      <div className="flex flex-col gap-3">
        {visibleRows.map(row => (
          <SettingsRow key={row.id} row={row} onClick={() => navigate(row.path)} />
        ))}
      </div>
    </div>
  );
}
